import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, ClipboardCheck, Globe, Instagram, Tag } from "lucide-react";
import avatar from "@/assets/jenifer-avatar.webp";
import { Logo } from "@/components/brand/Logo";
import { AuditButton, CtaReassurance, FreeAuditButton } from "@/components/ui/Cta";
import { LINK_HUB_PATH, QUIZ_PATH } from "@/config/constants";
import { usePageSeo } from "@/hooks/usePageSeo";
import { useLocale } from "@/i18n/useLocale";
import { trackEvent } from "@/utils/analytics";

type Copy = { es: string; en: string };

const COPY = {
  name: { es: "Jenifer Roelstraete", en: "Jenifer Roelstraete" },
  role: {
    es: "Estrategia digital para negocios con reservas",
    en: "Digital strategy for appointment-based businesses",
  },
  intro: {
    es: "Instagram, web y reservas trabajando juntos para que tu agenda se llene.",
    en: "Instagram, website and bookings working together to fill your diary.",
  },
  more: { es: "Explorá", en: "Explore" },
  site: { es: "Ir a la web", en: "Visit the website" },
} satisfies Record<string, Copy>;

const LINKS: readonly { to: string; icon: typeof Tag; label: Copy; detail: Copy }[] = [
  {
    to: QUIZ_PATH,
    icon: ClipboardCheck,
    label: { es: "Test: ¿tu Instagram llena tu agenda?", en: "Quiz: does your Instagram fill your diary?" },
    detail: { es: "2 minutos · resultado al momento", en: "2 minutes · instant result" },
  },
  {
    to: "/precios",
    icon: Tag,
    label: { es: "Planes y precios", en: "Plans and pricing" },
    detail: { es: "Gestión de Instagram desde 249 €/mes", en: "Instagram management from €249/month" },
  },
  {
    to: "/gestion-instagram",
    icon: Instagram,
    label: { es: "Gestión Estratégica de Instagram", en: "Strategic Instagram Management" },
    detail: { es: "Me encargo de tu Instagram", en: "I take care of your Instagram" },
  },
  {
    to: "/pagina-web",
    icon: Globe,
    label: { es: "Página Web Estratégica", en: "Strategic Website" },
    detail: { es: "Una web que convierte visitas en reservas", en: "A website that turns visits into bookings" },
  },
  {
    to: "/recursos",
    icon: BookOpen,
    label: { es: "Guía gratuita: tu bio que vende", en: "Free guide: a bio that sells" },
    detail: { es: "Descargala gratis", en: "Download it for free" },
  },
];

/**
 * The Instagram bio link (share it as /link?utm_source=instagram&utm_medium=bio).
 * One screen, no navigation: the audit first, then the paths that matter.
 */
export function LinkHubPage() {
  usePageSeo(LINK_HUB_PATH);
  const locale = useLocale();
  const tx = (copy: Copy) => copy[locale];

  return (
    <main id="main-content" tabIndex={-1} className="light-pool min-h-svh outline-none [--pool-x:50%] [--pool-y:0%]">
      <div className="mx-auto flex max-w-md flex-col items-center px-4 pb-16 pt-12 text-center sm:px-6">
        <img
          src={avatar}
          alt=""
          width={112}
          height={112}
          fetchPriority="high"
          className="size-24 rounded-full object-cover shadow-lit-lg ring-4 ring-white"
        />
        <h1 className="mt-5 font-display text-[1.875rem] font-semibold leading-tight text-brand-primary">{tx(COPY.name)}</h1>
        <p className="mt-1 text-sm font-medium text-teal-strong">{tx(COPY.role)}</p>
        <p className="mt-4 leading-relaxed text-ink-soft">{tx(COPY.intro)}</p>

        <div className="mt-8 flex w-full flex-col gap-3">
          <FreeAuditButton location="link_hub" className="w-full justify-center" />
          <CtaReassurance />
          <AuditButton location="link_hub" className="mt-1 w-full justify-center" />
        </div>

        <h2 className="mt-10 self-start text-sm font-semibold uppercase tracking-[0.14em] text-ink-muted">{tx(COPY.more)}</h2>
        <ul className="mt-4 flex w-full flex-col gap-3 text-left">
          {LINKS.map((item) => {
            const Icon = item.icon;
            return (
              <li key={item.to}>
                <Link
                  to={item.to}
                  data-tracked=""
                  onClick={() => trackEvent("cta_click", { location: "link_hub", target: item.to })}
                  className="group flex items-center gap-4 rounded-2xl border border-brand-primary/10 bg-white px-5 py-4 shadow-lit transition-colors hover:border-teal-strong"
                >
                  <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-teal-soft text-teal-strong">
                    <Icon aria-hidden="true" className="size-5" strokeWidth={1.75} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block font-semibold text-brand-primary">{tx(item.label)}</span>
                    <span className="block text-sm text-ink-muted">{tx(item.detail)}</span>
                  </span>
                  <ArrowRight
                    aria-hidden="true"
                    className="size-4 shrink-0 text-brand-primary transition-transform group-hover:translate-x-1"
                  />
                </Link>
              </li>
            );
          })}
        </ul>

        <Link to="/" className="mt-12 flex flex-col items-center gap-2 text-sm text-ink-muted hover:text-brand-primary">
          <Logo layout="horizontal" alt="" className="w-32" />
          {tx(COPY.site)}
        </Link>
      </div>
    </main>
  );
}
